import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Progress,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioPlanTurismo from './FormularioPlanTurismo';

const ESTATUS_COLORS: Record<string, string> = {
  borrador: 'gray',
  activo: 'green',
  concluido: 'blue',
};

const TablaPlanesTurismo: React.FC = () => {
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const itemsPerPage = 10;

  const anioActual = new Date().getFullYear();
  const aniosDisponibles = Array.from({ length: 8 }, (_, i) => anioActual - 2 + i);

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get('/turismo/planes');
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const datosFiltrados = data.filter((item) => {
    const matchAnio = filtroAnio ? item.anio?.toString() === filtroAnio : true;
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchBusqueda = busqueda
      ? item.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.responsable?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchAnio && matchEstatus && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleVer = (item: any) => {
    setSelectedItem(item);
    onOpenVer();
  };

  const handleEditar = (item: any) => {
    setSelectedItem(item);
    onOpenEditar();
  };

  const handleEliminar = async (item: any) => {
    const ok = window.confirm(`Eliminar el plan ${item.nombre}?`);
    if (!ok) return;
    try {
      await axiosInstance.delete(`/turismo/planes/${item.id}`);
      fetchData();
    } catch {
      // noop
    }
  };

  const handleGuardado = () => {
    onCloseNuevo();
    onCloseEditar();
    fetchData();
  };

  const handleExport = (format: 'pdf' | 'excel') => {
    const base = axiosInstance.defaults.baseURL || '';
    const url = `${base}/turismo/planes/export?format=${format}`;
    window.open(url, '_blank');
  };

  const formatFecha = (fecha: string) =>
    fecha ? new Date(fecha).toLocaleDateString('es-MX') : '-';

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por nombre o responsable..."
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            size="sm"
            w={{ base: 'full', md: '220px' }}
          />
          <Select
            placeholder="Filtrar por anio"
            value={filtroAnio}
            onChange={(e) => { setFiltroAnio(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="150px"
          >
            {aniosDisponibles.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Select>
          <Select
            placeholder="Filtrar por estatus"
            value={filtroEstatus}
            onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="160px"
          >
            <option value="borrador">Borrador</option>
            <option value="activo">Activo</option>
            <option value="concluido">Concluido</option>
          </Select>
        </HStack>

        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={() => handleExport('excel')}>
            Excel
          </Button>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="red" variant="outline" onClick={() => handleExport('pdf')}>
            PDF
          </Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>
            Nuevo plan
          </Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Anio</Th>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Fecha inicio</Th>
              <Th color="white" fontSize="xs">Fecha fin</Th>
              <Th color="white" fontSize="xs">Responsable</Th>
              <Th color="white" fontSize="xs" isNumeric>Avance</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">Sin registros</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: '#f7efe8' }}>
                  <Td fontWeight="semibold">{item.anio}</Td>
                  <Td maxW="240px"><Text noOfLines={1} title={item.nombre}>{item.nombre}</Text></Td>
                  <Td>{formatFecha(item.fechaInicio)}</Td>
                  <Td>{formatFecha(item.fechaFin)}</Td>
                  <Td>{item.responsable}</Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="xs" mb={1} textAlign="right">{item.avancePct || 0}%</Text>
                      <Progress value={item.avancePct || 0} size="xs" colorScheme={(item.avancePct || 0) >= 100 ? 'blue' : (item.avancePct || 0) >= 50 ? 'green' : 'orange'} borderRadius="full" w="80px" ml="auto" />
                    </Box>
                  </Td>
                  <Td>
                    <Badge colorScheme={ESTATUS_COLORS[item.estatus] || 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">
                      {item.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>Editar</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminar(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">
            Mostrando {indexPrimero + 1}-{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}
          </Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>�</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>�</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>
                  {page}
                </Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>�</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>�</Button>
          </HStack>
        </Flex>
      )}

      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">
            Detalle del plan
          </ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && <FormularioPlanTurismo modo="view" data={selectedItem} />}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">
            Editar plan
          </ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && (
              <FormularioPlanTurismo modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">
            Nuevo plan
          </ModalHeader>
          <ModalBody py={5} bg="white">
            <FormularioPlanTurismo modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaPlanesTurismo;


